import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Download, Mail, RefreshCw, Search, Inbox } from "lucide-react";

const TIER_LABEL = {
  activate: "Activate",
  accelerate: "Accelerate",
  interested: "Interested",
};

const TIER_COLOR = {
  activate: { bg: "rgba(255,168,115,0.18)", color: "var(--lh-cognac)" },
  accelerate: { bg: "var(--lh-peach)", color: "var(--lh-espresso-noir)" },
  interested: { bg: "rgba(31,26,21,0.08)", color: "var(--lh-espresso-noir)" },
};

function exportCSV(filename, header, rows) {
  if (rows.length === 0) {
    toast.message("Nothing to export.");
    return;
  }
  const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [header, ...rows].map((r) => r.map(escape).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  toast.success(`Exported ${rows.length} rows.`);
}

export default function DashLeads() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [tab, setTab] = useState("founding"); // "founding" | "herald"
  const [leads, setLeads] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tier, setTier] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      if (tab === "founding") {
        const { data } = await api.get("/leads/founding");
        setLeads(data);
      } else {
        const { data } = await api.get("/newsletter/subscribers");
        setSubs(data);
      }
    } catch {
      toast.error("Could not load.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) load();
    setQ("");
    // eslint-disable-next-line
  }, [isAdmin, tab]);

  if (!isAdmin) return <Navigate to="/dashboard" replace />;

  // -------- Founding leads filtering --------
  const filteredLeads = leads.filter((l) => {
    if (tier !== "all" && l.tier !== tier) return false;
    if (q) {
      const s = q.toLowerCase();
      const hay = `${l.name} ${l.email} ${l.business_name || ""} ${l.city || ""}`.toLowerCase();
      if (!hay.includes(s)) return false;
    }
    return true;
  });
  const tierCounts = {
    all: leads.length,
    activate: leads.filter((l) => l.tier === "activate").length,
    accelerate: leads.filter((l) => l.tier === "accelerate").length,
    interested: leads.filter((l) => l.tier === "interested").length,
  };

  // -------- HERald subscriber filtering --------
  const filteredSubs = subs.filter((s) => {
    if (!q) return true;
    const qs = q.toLowerCase();
    return (
      (s.email || "").toLowerCase().includes(qs) ||
      (s.name || "").toLowerCase().includes(qs) ||
      (s.source || "").toLowerCase().includes(qs)
    );
  });

  const exportFounding = () =>
    exportCSV(
      `localizeher-founding-leads-${new Date().toISOString().slice(0, 10)}.csv`,
      ["Name", "Email", "Business", "City", "Tier", "Source", "Message", "Created"],
      filteredLeads.map((l) => [
        l.name, l.email, l.business_name || "", l.city || "", l.tier || "",
        l.source || "", (l.message || "").replace(/\n/g, " "), l.created_at || "",
      ])
    );

  const exportHerald = () =>
    exportCSV(
      `localizeher-herald-subscribers-${new Date().toISOString().slice(0, 10)}.csv`,
      ["Name", "Email", "Phone", "Source", "Status", "Created"],
      filteredSubs.map((s) => [
        s.name, s.email, s.phone || "", s.source || "", s.status || "", s.created_at || "",
      ])
    );

  return (
    <div data-testid="dashboard-leads-page">
      <div className="overline text-[var(--lh-mauve)] mb-3">Admin · VIP List + Newsletter</div>
      <h1 className="font-serif-display text-5xl tracking-tighter leading-[0.95]">
        Your people, <em>in your corner.</em>
      </h1>
      <p className="mt-4 max-w-2xl font-medium text-[var(--lh-text)]">
        Every woman who's locked in a founding rate, and every reader subscribed to The HERald.
        Search, filter, export.
      </p>

      {/* Tabs */}
      <div className="mt-10 flex items-center gap-2 border-b border-[var(--lh-border)]">
        {[
          { key: "founding", label: "Founding Leads", count: leads.length },
          { key: "herald", label: "The HERald", count: subs.length },
        ].map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className="px-5 py-3 -mb-px text-sm font-semibold uppercase tracking-wider transition-colors"
            style={{
              color: tab === t.key ? "var(--lh-espresso-noir)" : "var(--lh-muted)",
              borderBottom: tab === t.key ? "2px solid var(--lh-espresso-noir)" : "2px solid transparent",
            }}
            data-testid={`leads-tab-${t.key}`}
          >
            {t.label}
            <span
              className="ml-2 px-2 py-0.5 rounded-full text-[0.65rem] font-bold"
              style={{
                background: tab === t.key ? "var(--lh-peach)" : "var(--lh-warm-linen)",
                color: tab === t.key ? "var(--lh-espresso-noir)" : "var(--lh-muted)",
              }}
            >
              {tab === t.key ? "" : ""}{t.key === "founding" ? leads.length : subs.length}
            </span>
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="mt-8 flex flex-col md:flex-row gap-3 items-stretch md:items-center">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--lh-greige)" }} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={tab === "founding" ? "Search name, email, business, city…" : "Search name, email, source…"}
            className="w-full pl-9 pr-3 py-2.5 rounded-full text-sm font-medium outline-none bg-white border border-[var(--lh-border)] focus:border-[var(--lh-peach)]"
            data-testid="leads-search"
          />
        </div>
        <button type="button" onClick={load} className="btn-ghost" data-testid="leads-refresh">
          <RefreshCw size={14} /> Refresh
        </button>
        <button
          type="button"
          onClick={tab === "founding" ? exportFounding : exportHerald}
          className="btn-primary"
          data-testid="leads-export"
        >
          <Download size={14} /> Export CSV
        </button>
      </div>

      {/* Tier filter chips — only on founding tab */}
      {tab === "founding" && (
        <div className="mt-5 flex flex-wrap gap-2">
          {["all", "activate", "accelerate", "interested"].map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setTier(k)}
              className="px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider transition-colors"
              style={{
                background: tier === k ? "var(--lh-peach)" : "transparent",
                color: tier === k ? "var(--lh-espresso-noir)" : "var(--lh-espresso-noir)",
                border: tier === k ? "1px solid var(--lh-peach)" : "1px solid var(--lh-border)",
              }}
              data-testid={`leads-tier-${k}`}
            >
              {k === "all" ? "All" : TIER_LABEL[k]} ({tierCounts[k]})
            </button>
          ))}
        </div>
      )}

      {/* Table */}
      <div className="mt-6 bg-white border border-[var(--lh-border)] rounded-2xl overflow-hidden" data-testid="leads-table">
        {loading ? (
          <div className="p-12 text-center overline" style={{ color: "var(--lh-muted)" }}>
            Loading…
          </div>
        ) : tab === "founding" ? (
          filteredLeads.length === 0 ? (
            <EmptyState message={leads.length === 0 ? "Your first founding lead will show up here." : "Try a different filter."} />
          ) : (
            <FoundingTable rows={filteredLeads} />
          )
        ) : filteredSubs.length === 0 ? (
          <EmptyState message={subs.length === 0 ? "No HERald subscribers yet." : "No subscribers match this search."} />
        ) : (
          <HeraldTable rows={filteredSubs} />
        )}
      </div>
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="p-12 text-center" data-testid="leads-empty">
      <Inbox size={28} style={{ color: "var(--lh-cognac)" }} className="mx-auto mb-4" aria-hidden="true" />
      <div className="font-display-bold text-2xl tracking-tighter">{message}</div>
    </div>
  );
}

function TH({ children }) {
  return <th className="overline px-5 py-3 text-left">{children}</th>;
}

function FoundingTable({ rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr style={{ background: "var(--lh-warm-linen)", color: "var(--lh-cognac)" }}>
            <TH>Name</TH><TH>Email</TH><TH>Business</TH><TH>City</TH><TH>Tier</TH><TH>When</TH>
          </tr>
        </thead>
        <tbody>
          {rows.map((l) => (
            <tr
              key={l.lead_id}
              className="border-t border-[var(--lh-border)] hover:bg-[var(--lh-warm-linen)]/60 transition-colors"
              data-testid={`lead-row-${l.lead_id}`}
            >
              <td className="px-5 py-4 font-semibold">
                {l.name}
                {l.message ? (
                  <div className="text-xs font-normal mt-1" style={{ color: "var(--lh-muted)" }}>
                    “{l.message.slice(0, 100)}{l.message.length > 100 ? "…" : ""}”
                  </div>
                ) : null}
              </td>
              <td className="px-5 py-4">
                <a href={`mailto:${l.email}`} className="text-[var(--lh-cognac)] hover:underline">{l.email}</a>
              </td>
              <td className="px-5 py-4">{l.business_name || <Dim />}</td>
              <td className="px-5 py-4">{l.city || <Dim />}</td>
              <td className="px-5 py-4">
                <span
                  className="px-2.5 py-1 rounded-full text-[0.65rem] font-semibold uppercase tracking-wider"
                  style={{
                    background: TIER_COLOR[l.tier || "interested"].bg,
                    color: TIER_COLOR[l.tier || "interested"].color,
                  }}
                >
                  {TIER_LABEL[l.tier || "interested"]}
                </span>
              </td>
              <td className="px-5 py-4 text-xs" style={{ color: "var(--lh-muted)" }}>
                {l.created_at ? new Date(l.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HeraldTable({ rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr style={{ background: "var(--lh-warm-linen)", color: "var(--lh-cognac)" }}>
            <TH>Name</TH><TH>Email</TH><TH>Phone</TH><TH>Source</TH><TH>Status</TH><TH>When</TH>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr
              key={s.sub_id}
              className="border-t border-[var(--lh-border)] hover:bg-[var(--lh-warm-linen)]/60 transition-colors"
              data-testid={`herald-row-${s.sub_id}`}
            >
              <td className="px-5 py-4 font-semibold">{s.name || <Dim />}</td>
              <td className="px-5 py-4">
                <a href={`mailto:${s.email}`} className="text-[var(--lh-cognac)] hover:underline">{s.email}</a>
              </td>
              <td className="px-5 py-4">{s.phone || <Dim />}</td>
              <td className="px-5 py-4">
                <span
                  className="px-2.5 py-1 rounded-full text-[0.65rem] font-semibold uppercase tracking-wider"
                  style={{ background: "rgba(255,168,115,0.18)", color: "var(--lh-cognac)" }}
                >
                  {s.source || "—"}
                </span>
              </td>
              <td className="px-5 py-4">
                <span
                  className="px-2.5 py-1 rounded-full text-[0.65rem] font-semibold uppercase tracking-wider"
                  style={{
                    background: s.status === "subscribed" ? "var(--lh-peach)" : "rgba(31,26,21,0.08)",
                    color: "var(--lh-espresso-noir)",
                  }}
                >
                  {s.status || "—"}
                </span>
              </td>
              <td className="px-5 py-4 text-xs" style={{ color: "var(--lh-muted)" }}>
                {s.created_at ? new Date(s.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Dim() {
  return <span style={{ color: "var(--lh-muted)" }}>—</span>;
}
